/**
 * Tutorial step catalog.
 *
 * Each step has:
 *   - id            unique key
 *   - title         headline
 *   - icon          lucide component
 *   - route         path to navigate to before the step is shown (nullable)
 *   - target        CSS selector to spotlight (nullable → concept card, centered)
 *   - placement     'bottom' | 'right' | 'top' | 'left' | 'center' (hint for card position)
 *   - bullets       short bullet points
 *   - example       optional visual JSX shown inside the card
 *   - adminOnly     step is skipped for non-admin users
 *   - sinceVersion  the tour version this step was introduced in. Used to
 *                   produce a "What's new" mini-tour for existing users after
 *                   we ship new features. Bump CURRENT_TUTORIAL_VERSION when
 *                   you add a step with a fresh sinceVersion.
 */
import {
  Sparkles,
  LayoutDashboard,
  Receipt,
  Paintbrush,
  ClipboardList,
  Tags,
  BarChart3,
  CreditCard,
  Users as UsersIcon,
  Settings as SettingsIcon,
  Search,
  KeyRound,
  Hammer,
  CheckCircle2,
  Wallet,
  Refrigerator,
  ArrowLeftRight,
  FileText,
  ReceiptText,
} from "lucide-react";

export const CURRENT_TUTORIAL_VERSION = 3;

// -------- Reusable example fragments --------
const ExampleSearch = () => (
  <div className="rounded-lg border border-border/60 bg-secondary/40 p-3 space-y-2">
    <div className="flex items-center gap-2 rounded-md border border-border/70 bg-white px-2.5 py-1.5 text-sm">
      <Search className="w-3.5 h-3.5 text-muted-foreground" />
      <span className="text-muted-foreground text-xs">Search orders, customers, PO…</span>
      <span className="ml-auto text-[10px] text-muted-foreground border border-border rounded px-1.5 py-0.5">
        Ctrl K
      </span>
    </div>
    <div className="space-y-1.5">
      {[
        { l: "ORD-00219 · David and Trisha DiGiovanni", r: "$721.50" },
        { l: "ORD-00218 · Mahir Mahir", r: "$2,567.50" },
      ].map((r, i) => (
        <div
          key={i}
          className="flex items-center justify-between rounded px-2.5 py-1.5 text-xs bg-white border border-border/60"
        >
          <span className="font-mono">{r.l}</span>
          <span className="num text-muted-foreground">{r.r}</span>
        </div>
      ))}
    </div>
  </div>
);

const ExamplePaidCredits = () => (
  <div className="grid grid-cols-2 gap-2">
    <div className="rounded-lg border border-border/60 p-3 bg-white">
      <div className="label-eyebrow">Paid (Stripe)</div>
      <div className="font-heading text-lg font-bold mt-1 num">$250,195</div>
      <div className="text-[10px] text-muted-foreground mt-0.5">87 orders</div>
    </div>
    <div className="rounded-lg border border-[#3d8c6e]/40 bg-[#3d8c6e]/5 p-3">
      <div className="label-eyebrow text-[#1f5f47]">Paid in credits</div>
      <div className="font-heading text-lg font-bold mt-1 num text-[#1f5f47]">$2,293</div>
      <div className="text-[10px] text-muted-foreground mt-0.5">4 orders · house account</div>
    </div>
  </div>
);

const ExampleActivity = () => (
  <div className="rounded-lg border border-border/60 bg-white p-3 space-y-2">
    {[
      { icon: Receipt, text: "New order ORD-00221", time: "2m ago" },
      { icon: CreditCard, text: "Stripe charge $4,445.08", time: "5m ago" },
      { icon: Paintbrush, text: "Batch #A-42 back from paint", time: "1h ago" },
    ].map((e, i) => (
      <div key={i} className="flex items-center gap-2.5 text-xs">
        <e.icon className="w-3.5 h-3.5 text-primary shrink-0" />
        <span className="flex-1 truncate">{e.text}</span>
        <span className="text-muted-foreground">{e.time}</span>
      </div>
    ))}
  </div>
);

const ExampleCatalog = () => (
  <div className="rounded-lg border border-border/60 bg-white overflow-hidden text-xs">
    <div className="grid grid-cols-[1fr_auto] gap-3 px-3 py-1.5 bg-secondary/50 label-eyebrow">
      <span>SKU</span>
      <span>Cost</span>
    </div>
    {[
      ["Hinge — soft close", "$1.20"],
      ["Screws (100pk)", "$3.40"],
      ["Paint · Alabaster qt", "$18.90"],
    ].map(([k, v], i) => (
      <div
        key={i}
        className="grid grid-cols-[1fr_auto] gap-3 px-3 py-1.5 border-t border-border/60"
      >
        <span>{k}</span>
        <span className="num text-muted-foreground">{v}</span>
      </div>
    ))}
  </div>
);

const ExamplePaintFlag = () => (
  <div className="rounded-lg border border-border/60 bg-white p-3 text-xs space-y-2">
    <div className="flex items-center justify-between">
      <span className="font-medium">Wall cabinet · 24"</span>
      <span className="text-[10px] pill bg-[#E5A93D]/15 text-[#7c5a14] border-[#E5A93D]/40 px-1.5 py-0.5 rounded border">
        needs paint
      </span>
    </div>
    <div className="flex items-center justify-between text-muted-foreground">
      <span>Alabaster (SW-7008)</span>
      <span className="num">Cost $42.10</span>
    </div>
  </div>
);

const ExampleWorkspaces = () => (
  <div className="rounded-lg border border-border/60 bg-secondary/40 p-2 space-y-1.5">
    {[
      { icon: Hammer, name: "Cabinet Depot", tag: "Ops Console", active: true },
      { icon: Refrigerator, name: "Appliance Depot", tag: "Documents Console", active: false },
    ].map((w, idx) => (
      <div
        key={idx}
        className={`flex items-center gap-2.5 rounded-md px-2.5 py-2 border text-xs ${
          w.active ? "bg-primary/10 border-primary/30" : "bg-white border-border/60"
        }`}
      >
        <div className="w-6 h-6 rounded bg-primary text-primary-foreground grid place-items-center shrink-0">
          <w.icon className="w-3.5 h-3.5" />
        </div>
        <div className="min-w-0">
          <div className="font-semibold truncate">{w.name}</div>
          <div className="text-[10px] tracking-[0.14em] uppercase text-muted-foreground">{w.tag}</div>
        </div>
        {w.active && <span className="ml-auto text-[9px] font-bold text-primary">CURRENT</span>}
      </div>
    ))}
  </div>
);

const ExampleOrderQuote = () => (
  <div className="grid grid-cols-2 gap-2">
    <div className="rounded-lg border border-[#0f766e]/40 bg-[#0f766e]/5 p-3">
      <div className="label-eyebrow text-[#0f766e]">Orders (revenue)</div>
      <div className="font-heading text-lg font-bold mt-1 num text-[#0f766e]">$16,188</div>
      <div className="text-[10px] text-muted-foreground mt-0.5">6 confirmed orders</div>
    </div>
    <div className="rounded-lg border border-[#1d4ed8]/40 bg-[#1d4ed8]/5 p-3">
      <div className="label-eyebrow text-[#1d4ed8]">Quotes (pipeline)</div>
      <div className="font-heading text-lg font-bold mt-1 num text-[#1d4ed8]">$21,422</div>
      <div className="text-[10px] text-muted-foreground mt-0.5">7 quotes · not yet confirmed</div>
    </div>
  </div>
);

// -------- The catalog --------
export const STEPS = [
  {
    id: "welcome",
    icon: Sparkles,
    title: "Welcome to Cabinet Depot Ops",
    route: "/",
    target: null,
    placement: "center",
    sinceVersion: 1,
    intro:
      "A quick tour of every feature. Each step highlights exactly what it's talking about — you'll see it as we go. About 2 minutes; skip anytime.",
    bullets: [
      "Live orders from Base44 · Stripe reconciliation · Paint tracking",
      "The panel follows the feature — we'll ring the actual button, card, or table",
      "Replay this anytime from Settings → Replay tutorial",
    ],
  },
  {
    id: "sidebar",
    icon: Hammer,
    title: "Your workspace navigation",
    route: "/",
    target: '[data-testid="nav-orders"]',
    placement: "right",
    sinceVersion: 1,
    bullets: [
      "Every top-level tool lives here on the left",
      "Dashboard for KPIs, Orders/Paint for daily ops, Stripe for money",
      "Admin-only: Team + Danger zone in Settings",
    ],
  },
  {
    id: "kpi-cards",
    icon: LayoutDashboard,
    title: "Dashboard — north-star KPIs",
    route: "/",
    target: '[data-testid="kpi-profit"]',
    placement: "bottom",
    sinceVersion: 1,
    bullets: [
      "Profit, revenue, COGS, open orders — updated every sync",
      "Cards for items in paint and ready-to-ship give warehouse pulse",
      "Filter by store from the top-right to slice per location",
    ],
  },
  {
    id: "recent-activity",
    icon: LayoutDashboard,
    title: "Recent activity feed",
    route: "/",
    target: '[data-testid="recent-activity"]',
    placement: "left",
    sinceVersion: 1,
    bullets: [
      "New orders, Stripe charges, deliveries, and paint returns — live",
      "Auto-refreshes every 60 seconds",
      "Click any item to jump to the underlying order",
    ],
    example: <ExampleActivity />,
  },
  {
    id: "sync-base44",
    icon: LayoutDashboard,
    title: "Base44 auto-sync",
    route: "/",
    target: '[data-testid="sync-base44-btn"]',
    placement: "bottom",
    sinceVersion: 1,
    bullets: [
      "Orders sync from Base44 every 5 minutes automatically",
      "Click this button for an instant pull if you can't wait",
      "The Orders page shows the last sync time + countdown to the next one",
    ],
  },
  {
    id: "orders-list",
    icon: Receipt,
    title: "Orders — the source of truth",
    route: "/orders",
    target: '[data-testid="orders-sync-status"]',
    placement: "bottom",
    sinceVersion: 1,
    bullets: [
      "Every Base44 order, filterable by store and status",
      "Click any row to open the full order detail",
      "The badge shows how fresh the data is + when the next auto-sync runs",
    ],
  },
  {
    id: "orders-search",
    icon: Receipt,
    title: "Find any order in seconds",
    route: "/orders",
    target: '[data-testid="orders-search"]',
    placement: "bottom",
    sinceVersion: 1,
    bullets: [
      "Search by order #, customer name, PO, or tracking",
      "Status filter to the right — Building, Picking, Ready to ship, Delivered, etc.",
      "Store filter narrows to a single showroom's orders",
    ],
  },
  {
    id: "order-detail-paint",
    icon: Receipt,
    title: "Paint flags & margin per item",
    route: "/orders",
    target: null,
    placement: "center",
    sinceVersion: 1,
    bullets: [
      "Open any order → flag items that need paint + assign a color",
      "Unit costs pull from your Cost Catalog for real-time margin",
      "Customer, PO, deposit, and payment info in one view",
    ],
    example: <ExamplePaintFlag />,
  },
  {
    id: "paint-board",
    icon: Paintbrush,
    title: "Paint Board — live vendor queue",
    route: "/paint",
    target: '[data-testid="paint-kpis"]',
    placement: "bottom",
    sinceVersion: 1,
    bullets: [
      "Read-only mirror of your external Paint Scanner API",
      "KPI strip: pending, in-vendor, ready-to-return counts at a glance",
    ],
  },
  {
    id: "paint-tabs",
    icon: Paintbrush,
    title: "Queue vs Batches",
    route: "/paint",
    target: '[data-testid="paint-tab-queue"]',
    placement: "bottom",
    sinceVersion: 1,
    bullets: [
      "'Queue' — every item flagged for paint, grouped by status",
      "'Batches' — vendor packages with printable manifest URLs from the scanner",
      "Group-by dropdown switches between color / order / status views",
    ],
  },
  {
    id: "manifests",
    icon: ClipboardList,
    title: "Manifests — printable dispatch sheets",
    route: "/manifests",
    target: '[data-testid="new-manifest-btn"]',
    placement: "bottom",
    sinceVersion: 1,
    bullets: [
      "Build paint_out, paint_in, and delivery manifests",
      "Attach items + driver + destination notes",
      "Print directly from the browser for the warehouse or driver",
    ],
  },
  {
    id: "catalog",
    icon: Tags,
    title: "Cost Catalog — every unit cost",
    route: "/catalog",
    target: '[data-testid="catalog-search"]',
    placement: "bottom",
    sinceVersion: 1,
    bullets: [
      "Cabinets, doors, hinges, screws, paint — every SKU with its cost",
      "Feeds the profit math on every order",
      "Search bar filters by name or SKU as you type",
    ],
    example: <ExampleCatalog />,
  },
  {
    id: "catalog-tools",
    icon: Tags,
    title: "Catalog · import & export",
    route: "/catalog",
    target: '[data-testid="catalog-import"]',
    placement: "bottom",
    sinceVersion: 1,
    bullets: [
      "'Import CSV' — bulk-upload cost changes without hand-editing",
      "'Export CSV' — pull the whole catalog out for reference or backup",
      "'Bootstrap' seeds common consumables in one click if you're starting fresh",
    ],
  },
  {
    id: "reports",
    icon: BarChart3,
    title: "Reports — Daily P&L",
    route: "/reports",
    target: '[data-testid="tab-daily"]',
    placement: "bottom",
    sinceVersion: 1,
    bullets: [
      "Daily P&L: sales, refunds, expenses, net profit",
      "Pick any date to snapshot that day's numbers",
      "Profit trend chart plots the month at a glance",
    ],
  },
  {
    id: "reports-transactions",
    icon: BarChart3,
    title: "Transactions & CSV export",
    route: "/reports",
    target: '[data-testid="tab-transactions"]',
    placement: "bottom",
    sinceVersion: 1,
    bullets: [
      "Switch to Transactions for a filterable date-range view",
      "'Export CSV' downloads the exact rows currently visible",
      "'Payouts' tab pulls Stripe payouts side-by-side with sales",
    ],
  },
  {
    id: "stripe-overview",
    icon: Wallet,
    title: "Stripe — money overview",
    route: "/stripe",
    target: '[data-testid="stripe-balance-available"]',
    placement: "bottom",
    sinceVersion: 1,
    bullets: [
      "Available balance, pending, and instant-payout amounts",
      "This-month rollup: gross, refunds, fees, merchant net",
      "Payout destinations + upcoming payouts",
    ],
  },
  {
    id: "stripe-payments-tab",
    icon: CreditCard,
    title: "Stripe → Payments (reconciliation)",
    route: "/stripe?tab=payments",
    target: '[data-testid="payments-filter"]',
    placement: "bottom",
    sinceVersion: 1,
    bullets: [
      "The single answer to 'is this order paid?'",
      "5 buckets: Paid (Stripe) · Paid in credits · Partial · On the way · Unpaid",
      "'Resync from Stripe' pulls the latest charges on demand",
    ],
  },
  {
    id: "paid-in-credits",
    icon: Wallet,
    title: "NEW · Paid in credits bucket",
    route: "/stripe?tab=payments",
    target: '[data-testid="payments-summary-paid-credit"]',
    placement: "bottom",
    sinceVersion: 2,
    isNew: true,
    bullets: [
      "Orders fully covered by house-account credits get their own KPI",
      "Click the card to instantly filter the table to those orders",
      "Partial orders that used SOME credit show a 'Credits: $X' badge on the row",
    ],
    example: <ExamplePaidCredits />,
  },
  {
    id: "stripe-more-tabs",
    icon: CreditCard,
    title: "Deep-dive Stripe tabs",
    route: "/stripe?tab=ledger",
    target: '[data-testid="stripe-tab-ledger"]',
    placement: "bottom",
    sinceVersion: 1,
    bullets: [
      "Charges — every card / ACH charge with fee + net breakdown",
      "Refunds — full audit of money going back",
      "Balance ledger — every txn, filterable by type (payouts, fees, adjustments)",
      "Disputes — chargebacks with evidence-due dates so you never miss one",
    ],
  },
  {
    id: "search",
    icon: Search,
    title: "Global search — ⌘K",
    route: null,
    target: '[data-testid="global-search-trigger"]',
    placement: "bottom",
    sinceVersion: 1,
    bullets: [
      "Press ⌘K (or Ctrl+K) anywhere to search",
      "Works on order numbers, PO, tracking, customers, SKUs",
      "The fastest way to jump to a specific record",
    ],
    example: <ExampleSearch />,
  },
  {
    id: "change-password",
    icon: KeyRound,
    title: "Change your password",
    route: null,
    target: '[data-testid="change-password-btn"]',
    placement: "top",
    sinceVersion: 1,
    bullets: [
      "Bottom-left corner — available to every user",
      "Requires your current password to change",
      "Changing your password ends all other active sessions",
    ],
  },
  {
    id: "team",
    icon: UsersIcon,
    title: "Team management (admin)",
    route: "/team",
    target: '[data-testid="team-add-user-btn"]',
    placement: "left",
    adminOnly: true,
    sinceVersion: 1,
    bullets: [
      "Invite users, assign roles, set initial passwords",
      "Reset someone's password → their live session is killed instantly",
      "Sparkle button next to each user resets THEIR tutorial",
      "The last admin cannot be demoted or deleted (safety guard)",
    ],
  },
  {
    id: "settings",
    icon: SettingsIcon,
    title: "Settings — integrations & replay",
    route: "/settings",
    target: '[data-testid="replay-tutorial-btn"]',
    placement: "left",
    sinceVersion: 1,
    bullets: [
      "Base44 + Stripe connection status and last sync",
      "'Resync all' pulls every order fresh from Base44",
      "Replay THIS tutorial anytime from here",
    ],
    adminBullets: [
      "Admin-only Danger zone: purge test debris or wipe deliveries/manifests",
    ],
  },
  {
    id: "workspace-switcher",
    icon: ArrowLeftRight,
    title: "NEW · Two businesses, one login",
    route: "/",
    target: '[data-testid="workspace-switcher"]',
    placement: "right",
    sinceVersion: 3,
    isNew: true,
    bullets: [
      "Switch between Cabinet Depot and Appliance Depot right here at the top",
      "The whole sidebar, dashboard, and pages swap to the workspace you pick",
      "Your choice sticks between visits — and the nav follows the page you open",
    ],
    example: <ExampleWorkspaces />,
  },
  {
    id: "appliance-dashboard",
    icon: Refrigerator,
    title: "Appliance Depot — Orders vs Quotes",
    route: "/appliance",
    target: '[data-testid="appliance-kpi-orders"]',
    placement: "bottom",
    sinceVersion: 3,
    isNew: true,
    bullets: [
      "Orders (revenue) are confirmed sales — your headline number",
      "Quotes (pipeline) are proposals not yet confirmed, tracked separately",
      "Top dealers rank by confirmed order revenue, with quoted totals alongside",
    ],
    example: <ExampleOrderQuote />,
  },
  {
    id: "appliance-documents",
    icon: FileText,
    title: "Browse every order & quote",
    route: "/appliance/documents",
    target: '[data-testid="appliance-type-filter"]',
    placement: "bottom",
    sinceVersion: 3,
    isNew: true,
    bullets: [
      "Search by document number, customer, or dealer",
      "Filter to just Orders or just Quotes in one click",
      "Open any row for line items (with photos), dealer/customer info, and totals",
    ],
  },
  {
    id: "appliance-report",
    icon: ReceiptText,
    title: "Appliance daily report email",
    route: "/appliance/report",
    target: '[data-testid="appliance-email-card"]',
    placement: "bottom",
    sinceVersion: 3,
    isNew: true,
    bullets: [
      "Toggle on a daily email of confirmed orders and pipeline quotes",
      "Pick the local send time; hit 'Send test now' to preview it in your inbox",
      "Quiet days won't send an email — reports only go out when there's activity",
    ],
  },
  {
    id: "done",
    icon: CheckCircle2,
    title: "You're all set",
    route: "/",
    target: null,
    placement: "center",
    sinceVersion: 1,
    intro:
      "That's the full console. New features will pop back up as a short 'What's new' tour when they ship. Happy shipping!",
    bullets: [
      "Replay this tour anytime → Settings → Replay tutorial",
      "Admins can reset any teammate's tutorial from the Team page",
    ],
  },
];

/**
 * Given a user's saved tutorial_version, return the steps we should show.
 * - null / undefined → full onboarding tour
 * - < CURRENT_TUTORIAL_VERSION → only the new steps (What's new mini-tour)
 * - >= CURRENT_TUTORIAL_VERSION → empty (nothing to show; caller shouldn't open)
 */
export function stepsForUser(user) {
  const isAdmin = user?.role === "admin";
  const savedVersion = user?.tutorial_version;

  const roleFiltered = STEPS.filter((s) => !s.adminOnly || isAdmin);

  // First-time viewer — full tour
  if (savedVersion == null) return { steps: roleFiltered, mode: "full" };

  // They saw an older version — only surface new steps
  if (savedVersion < CURRENT_TUTORIAL_VERSION) {
    const newOnly = roleFiltered.filter(
      (s) => (s.sinceVersion || 1) > savedVersion,
    );
    if (newOnly.length === 0) return { steps: [], mode: "up-to-date" };
    return { steps: newOnly, mode: "whats-new" };
  }

  // Up to date
  return { steps: [], mode: "up-to-date" };
}
